import React, { useState, useRef, useEffect } from 'react';
import { OrderStatus, OrderStatusMetadata } from '../../../types/order-status';
import styles from './ui-status-dropdown.module.css';

interface StatusDropdownProps {
  currentStatus: OrderStatus;
  availableStatuses: OrderStatusMetadata[];
  onStatusChange: (newStatus: OrderStatus) => void;
  disabled?: boolean;
  label?: string;
}

export function StatusDropdown({
  currentStatus,
  availableStatuses,
  onStatusChange,
  disabled = false,
  label = 'Status'
}: StatusDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && triggerRef.current && dropdownRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.scrollHeight;
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;

      // Open upward if there's more space above and not enough space below
      setOpenUpward(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
    }
  }, [isOpen]);

  const handleStatusClick = (status: OrderStatus) => {
    if (status !== currentStatus) {
      onStatusChange(status);
    }
    setIsOpen(false);
  };

  const currentStatusMeta = availableStatuses.find(s => s.value === currentStatus);

  return (
    <div className={styles.container}>
      {label && <label className={styles.label}>{label}</label>}
      <div className={styles.dropdownWrapper}>
        <button
          ref={triggerRef}
          className={styles.trigger}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className={styles.currentStatus}>
            {currentStatusMeta?.label || currentStatus}
          </span>
          <span className={styles.arrow}>▼</span>
        </button>

        {isOpen && (
          <>
            <div
              className={styles.backdrop}
              onClick={() => setIsOpen(false)}
            />
            <div
              ref={dropdownRef}
              className={`${styles.dropdown} ${openUpward ? styles.dropdownUpward : ''}`}
              role="listbox"
            >
              {availableStatuses.map((status) => (
                <button
                  key={status.value}
                  className={`${styles.option} ${status.value === currentStatus ? styles.active : ''}`}
                  onClick={() => handleStatusClick(status.value)}
                  role="option"
                  aria-selected={status.value === currentStatus}
                >
                  <div className={styles.optionLabel}>{status.label}</div>
                  <div className={styles.optionDescription}>{status.description}</div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
